import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getApiUrl } from '../config/api';
import RatingBadge from './RatingBadge';
import RatingDisplay from './RatingDisplay';
import { useToast } from '../context/ToastContext';
import { ensureAuthenticated } from '../services/authGuardService';
import { getStoredUser } from '../services/subscriptionService';
import { getApiErrorMessage, readJsonSafe } from '../services/apiResponseService';
import styles from '../styles/modules/SearchPages.module.css';
import staffStyles from '../styles/modules/SearchStaff.module.css';
import headerStyles from '../styles/modules/Header.module.css';

interface Experience {
    position: string;
    company: string;
    startMonth: string;
    startYear: number;
    endMonth?: string;
    endYear?: number;
    description?: string;
}

interface Bartender {
    _id: string;
    username: string;
    fullName: string;
    email: string;
    phone?: string;
    role: string;
    experience?: Experience[];
    profilePicture?: string;
}

interface AvailabilityResult {
    _id: string;
    date: string | null;
    startTime: string | null;
    place: string | null;
    needsTransport: boolean;
    status: string | null;
    notes?: string | null;
    bartender: Bartender;
    createdAt: string;
    updatedAt: string;
}

interface SearchFilters {
    date: string;
    place: string;
    role: string;
    needsTransport: string;
    showUnavailableUsers: boolean;
}

interface AvailabilitySearchResponse {
    data: AvailabilityResult[];
}

const normalizeAvailabilityResults = (payload: unknown): AvailabilityResult[] => {
    if (Array.isArray(payload)) {
        return payload as AvailabilityResult[];
    }

    if (payload && typeof payload === 'object' && 'data' in payload) {
        const data = (payload as AvailabilitySearchResponse).data;
        if (Array.isArray(data)) {
            return data;
        }
    }

    return [];
};

const SearchStaff: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [results, setResults] = useState<AvailabilityResult[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
    const [creatingEngagement, setCreatingEngagement] = useState<string | null>(null);
    const [selectedStaffForReviews, setSelectedStaffForReviews] = useState<string | null>(null);
    const currentUser = useMemo(() => getStoredUser(), []);
    const quickSearchExpiry = currentUser?.activeQuickSearchExpiry ?? null;

    // Staff roles for filtering
    const staffRoles = [
        'Barman',
        'Bartender',
        'Waiter',
        'Waitress',
        'Chef',
        'Sous Chef',
        'Cook',
        'Dishwasher',
        'Host/Hostess',
        'Sommelier',
        'Busser',
        'Kitchen Porter',
        'Line Cook',
        'Pastry Chef',
        'Counter Worker',
        'Pizza Maker',
        'Grill Master',
        'Baker',
        'Confectioner',
        'Hostess'
    ];
    
    // Filter state
    const [filters, setFilters] = useState<SearchFilters>({
        date: '',
        place: '',
        role: '',
        needsTransport: '',
        showUnavailableUsers: false
    });

    // Load all available staff on component mount
    useEffect(() => {
        const loadStaff = async () => {
            setLoading(true);
            setError('');
            
            try {
                const params = new URLSearchParams();
                if (filters.date) params.append('date', filters.date);
                if (filters.place.trim()) params.append('place', filters.place.trim());
                if (filters.role) params.append('role', filters.role);
                if (filters.needsTransport) params.append('needsTransport', filters.needsTransport);
                if (filters.showUnavailableUsers) params.append('showUnavailableUsers', 'true');

                const queryString = params.toString();
                const url = getApiUrl(`availability/search${queryString ? `?${queryString}` : ''}`);

                const response = await fetch(url);

                if (!response.ok) {
                    throw new Error(`Search failed: ${response.statusText}`);
                }

                const payload = await response.json();
                setResults(normalizeAvailabilityResults(payload));
            } catch (error) {
                console.error('Error loading staff:', error);
                setError(error instanceof Error ? error.message : 'Failed to load staff');
            } finally {
                setLoading(false);
            }
        };

        loadStaff();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const searchStaff = async () => {
        setLoading(true);
        setError('');
        
        try {
            // Build query parameters
            const params = new URLSearchParams();
            if (filters.date) params.append('date', filters.date);
            if (filters.place.trim()) params.append('place', filters.place.trim());
            if (filters.role) params.append('role', filters.role);
            if (filters.needsTransport) params.append('needsTransport', filters.needsTransport);
            if (filters.showUnavailableUsers) params.append('showUnavailableUsers', 'true');

            const queryString = params.toString();
            const url = getApiUrl(`availability/search${queryString ? `?${queryString}` : ''}`);
            
            console.log('Searching with URL:', url);

            const response = await fetch(url);

            if (!response.ok) {
                throw new Error(`Search failed: ${response.statusText}`);
            }

            const payload = await response.json();
            const normalizedResults = normalizeAvailabilityResults(payload);
            setResults(normalizedResults);
            console.log('Search results:', normalizedResults);
        } catch (error) {
            console.error('Error searching staff:', error);
            setError(error instanceof Error ? error.message : 'Failed to search staff');
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        const nextValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;

        setFilters(prev => ({
            ...prev,
            [name]: nextValue
        }));
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        searchStaff();
    };

    const handleClearFilters = () => {
        setFilters({
            date: '',
            place: '',
            role: '',
            needsTransport: '',
            showUnavailableUsers: false
        });
        // Search with cleared filters
        setTimeout(() => searchStaff(), 0);
    };

    const toggleCardExpanded = (id: string) => {
        setExpandedCards(prev => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            } else {
                newSet.add(id);
            }
            return newSet;
        });
    };

    const handleOpenStaffReviews = (staffId: string) => {
        setSelectedStaffForReviews(staffId);
    };

    const handleContactStaff = (bartender: Bartender) => {
        const contactHref = bartender.phone ? `tel:${bartender.phone}` : `mailto:${bartender.email}`;
        window.location.href = contactHref;
    };

    const handleCreateEngagement = async (availability: AvailabilityResult) => {
        if (!availability.date || !availability.startTime || !availability.place) {
            showToast({
                message: t('searchStaff.engagementUnavailable') || 'This user has no availability slot to create an engagement from.',
                type: 'error'
            });
            return;
        }

        const auth = ensureAuthenticated();

        if (!auth.allowed) {
            showToast({ message: t('searchStaff.engagementLoginRequired') || 'You must be logged in to create an engagement', type: 'error' });
            navigate(auth.redirectTo);
            return;
        }

        const token = auth.token;
        const currentUser = auth.user;
        setCreatingEngagement(availability._id);

        // Debug: log to verify correct IDs
        console.log('SearchStaff - Creating engagement:', {
            employerId: currentUser.id || currentUser._id,
            bartenderId: availability.bartender._id,
            availability: availability
        });

        try {
            const engagementData = {
                employerId: currentUser.id || currentUser._id,
                bartenderId: availability.bartender._id,
                availabilityId: availability._id,
                jobRole: availability.bartender.role,
                date: availability.date,
                startTime: availability.startTime,
                place: availability.place,
                facilityName: '',
                payRate: '',
                description: '',
                notes: availability.notes || ''
            };

            const response = await fetch(getApiUrl('engagements'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(engagementData)
            });

            if (!response.ok) {
                const errorData = await readJsonSafe<any>(response);
                throw new Error(getApiErrorMessage(response, t('searchStaff.engagementCreateFailed') || 'Failed to create engagement', errorData));
            }

            const result = await response.json();
            showToast({ message: t('searchStaff.engagementCreateSuccess'), type: 'success' });
            console.log('Engagement created:', result);
        } catch (error) {
            console.error('Error creating engagement:', error);
            showToast({ message: error instanceof Error ? error.message : t('searchStaff.engagementCreateFailed'), type: 'error' });
        } finally {
            setCreatingEngagement(null);
        }
    };

    const handleBackClick = () => {
        navigate('/dashboard');
    };

    const formatDate = (dateString?: string | null): string => {
        if (!dateString) {
            return t('searchStaff.noAvailabilityDetails') || 'No availability details';
        }

        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) {
            return t('searchStaff.noAvailabilityDetails') || 'No availability details';
        }

        const locale = t('common.locale') || 'en-US';
        return date.toLocaleDateString(locale, { 
            weekday: 'short',
            year: 'numeric', 
            month: 'short', 
            day: 'numeric' 
        });
    };

    const formatTime = (time?: string | null): string => {
        if (!time) {
            return t('searchStaff.notProvided') || 'Not provided';
        }

        // Convert 24h to 12h format
        const [hours, minutes] = time.split(':');
        if (hours === undefined || minutes === undefined) {
            return t('searchStaff.notProvided') || 'Not provided';
        }

        const hour = parseInt(hours);
        if (Number.isNaN(hour)) {
            return t('searchStaff.notProvided') || 'Not provided';
        }

        const ampm = hour >= 12 ? 'PM' : 'AM';
        const hour12 = hour % 12 || 12;
        return `${hour12}:${minutes} ${ampm}`;
    };

    return (
        <div className={styles.pageContainer}>
            <header className={headerStyles.header}>
                <button onClick={handleBackClick} className={`${headerStyles.backButton} ${headerStyles.backButtonAbsolute}`}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 12H5M12 19l-7-7 7-7"/>
                    </svg>
                </button>
                <h1 className={headerStyles.titleDark}>{t('searchStaff.title')}</h1>
                <div></div>
            </header>

            {quickSearchExpiry && (
                <div className={staffStyles.searchExpiryBanner}>
                    <span>⏱ {t('subscription.quickSearchActiveUntil')}: <strong>{new Date(quickSearchExpiry).toLocaleString()}</strong></span>
                </div>
            )}

            {/* Filters Form */}
            <form onSubmit={handleSearch} className={styles.filtersForm}>
                <div className={styles.filtersGrid}>
                    <div className={styles.filterGroup}>
                        <label htmlFor="date" className={styles.filterLabel}>{t('searchStaff.date')}</label>
                        <input
                            type="date"
                            id="date"
                            name="date"
                            value={filters.date}
                            onChange={handleFilterChange}
                            className={styles.filterInput}
                            min={new Date().toISOString().split('T')[0]}
                        />
                    </div>

                    <div className={styles.filterGroup}>
                        <label htmlFor="place" className={styles.filterLabel}>{t('searchStaff.location')}</label>
                        <input
                            type="text"
                            id="place"
                            name="place"
                            value={filters.place}
                            onChange={handleFilterChange}
                            className={styles.filterInput}
                            placeholder={t('searchStaff.locationPlaceholder')}
                        />
                    </div>

                    <div className={styles.filterGroup}>
                        <label htmlFor="role" className={styles.filterLabel}>{t('searchStaff.role')}</label>
                        <select
                            id="role"
                            name="role"
                            value={filters.role}
                            onChange={handleFilterChange}
                            className={styles.filterInput}
                        >
                            <option value="">{t('searchStaff.allRoles')}</option>
                            {staffRoles.map(role => (
                                <option key={role} value={role}>
                                    {t(`roles.${role.toLowerCase().replace(/\s+/g, '').replace('/', '').replace('&', '')}`)}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={styles.filterGroup}>
                        <label htmlFor="needsTransport" className={styles.filterLabel}>{t('searchStaff.transport')}</label>
                        <select
                            id="needsTransport"
                            name="needsTransport"
                            value={filters.needsTransport}
                            onChange={handleFilterChange}
                            className={styles.filterInput}
                        >
                            <option value="">{t('searchStaff.any')}</option>
                            <option value="true">{t('searchStaff.needsTransport')}</option>
                            <option value="false">{t('searchStaff.noTransportNeeded')}</option>
                        </select>
                    </div>

                    <div className={styles.filterGroup}>
                        <label htmlFor="showUnavailableUsers" className={`${styles.filterLabel} ${styles.checkboxLabel}`}>
                            <input
                                type="checkbox"
                                id="showUnavailableUsers"
                                name="showUnavailableUsers"
                                checked={filters.showUnavailableUsers}
                                onChange={handleFilterChange}
                                className={styles.filterCheckbox}
                            />
                            {t('searchStaff.showUnavailableUsers') || 'Show unavailable users'}
                        </label>
                    </div>
                </div>

                <div className={styles.filtersActions}>
                    <button type="submit" className={styles.searchButton} disabled={loading}>
                        {loading ? t('searchStaff.searching') : t('searchStaff.search')}
                    </button>
                    <button type="button" onClick={handleClearFilters} className={styles.clearButton}>
                        {t('searchStaff.clearFilters')}
                    </button>
                </div>
            </form>

            {/* Error Message */}
            {error && (
                <div className={styles.error}>
                    {error}
                </div>
            )}

            {/* Results */}
            <div className={styles.resultsSection}>
                <div className={styles.resultsHeader}>
                    <h2 className={styles.resultsHeader}>
                        {loading ? t('searchStaff.searching') : `${t('searchStaff.found')} ${results.length} ${results.length !== 1 ? t('searchStaff.staffMembers') : t('searchStaff.staffMember')}`}
                    </h2>
                </div>

                {loading ? (
                    <div className={styles.loading}>{t('common.loading')}</div>
                ) : results.length === 0 ? (
                    <div className={styles.noResults}>
                        <p>{t('searchStaff.noResults')}</p>
                        <p>{t('searchStaff.tryAdjusting')}</p>
                    </div>
                ) : (
                    <div className={styles.resultsGrid}>
                        {results.map((result) => {
                            const isExpanded = expandedCards.has(result._id);
                            const bartender = result.bartender;
                            const hasAvailabilityDetails = Boolean(result.date || result.startTime || result.place);
                            const status = result.status || 'unavailable';
                            
                            return (
                                <div key={result._id} className={staffStyles.staffCard}>
                                    <div className={staffStyles.cardHeader}>
                                        <div className={staffStyles.staffMainInfo}>
                                            <div className={staffStyles.staffAvatar}>
                                                {bartender.profilePicture ? (
                                                    <img 
                                                        src={bartender.profilePicture} 
                                                        alt={bartender.fullName}
                                                        className={staffStyles.staffProfilePicture}
                                                    />
                                                ) : (
                                                    bartender.fullName.charAt(0).toUpperCase()
                                                )}
                                            </div>
                                            <div className={staffStyles.staffDetails}>
                                                <h3 className={staffStyles.staffName}>{bartender.fullName}</h3>
                                                <div 
                                                    className={staffStyles.ratingContainer}
                                                    onClick={() => handleOpenStaffReviews(bartender._id)}
                                                    style={{ cursor: 'pointer' }}
                                                    role="button"
                                                    tabIndex={0}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            handleOpenStaffReviews(bartender._id);
                                                        }
                                                    }}
                                                >
                                                    <RatingBadge userId={bartender._id} />
                                                </div>
                                                <p className={staffStyles.staffUsername}>@{bartender.username}</p>
                                                <p className={staffStyles.staffRole}>{t(`roles.${bartender.role.toLowerCase().replace(/\s+/g, '').replace('/', '').replace('&', '')}`)}</p>
                                            </div>
                                        </div>
                                        <div className={`${staffStyles.statusBadge} ${status === 'available' ? staffStyles.statusAvailable : status === 'booked' ? staffStyles.statusBooked : staffStyles.statusUnavailable}`}>
                                            {t(`searchStaff.status.${status}`)}
                                        </div>
                                    </div>

                                    <div className={staffStyles.cardBody}>
                                        <div className={staffStyles.staffInfo}>
                                            <div className={staffStyles.infoRow}>
                                                <span className={staffStyles.infoIcon}>📅</span>
                                                <span className={staffStyles.infoText}>{formatDate(result.date)}</span>
                                            </div>
                                            <div className={staffStyles.infoRow}>
                                                <span className={staffStyles.infoIcon}>🕒</span>
                                                <span className={staffStyles.infoText}>
                                                    {formatTime(result.startTime)}
                                                </span>
                                            </div>
                                            <div className={staffStyles.infoRow}>
                                                <span className={staffStyles.infoIcon}>📍</span>
                                                <span className={staffStyles.infoText}>{result.place || (t('searchStaff.notProvided') || 'Not provided')}</span>
                                            </div>
                                            {hasAvailabilityDetails && result.needsTransport && (
                                                <div className={staffStyles.infoRow}>
                                                    <span className={staffStyles.infoIcon}>🚗</span>
                                                    <span className={staffStyles.infoText}>{t('searchStaff.transportNeeded')}</span>
                                                </div>
                                            )}
                                        </div>

                                        {result.notes && (
                                            <div className={staffStyles.notesSection}>
                                                <strong>{t('searchStaff.notes')}:</strong> {result.notes}
                                            </div>
                                        )}

                                        {/* Experience Section */}
                                        {bartender.experience && bartender.experience.length > 0 && (
                                            <div className={staffStyles.experienceSection}>
                                                <button 
                                                    className={staffStyles.toggleExperienceBtn}
                                                    onClick={() => toggleCardExpanded(result._id)}
                                                >
                                                    {isExpanded ? '▼' : '▶'} {t('searchStaff.experience')} ({bartender.experience.length})
                                                </button>
                                                
                                                {isExpanded && (
                                                    <div className={staffStyles.experienceList}>
                                                        {bartender.experience.map((exp, index) => (
                                                            <div key={index} className={staffStyles.experienceItem}>
                                                                <div className={staffStyles.experienceHeader}>
                                                                    <strong className={staffStyles.experiencePosition}>{exp.position}</strong>
                                                                    <span className={staffStyles.experienceCompany}>{exp.company}</span>
                                                                </div>
                                                                <div className={staffStyles.experiencePeriod}>
                                                                    {exp.startMonth} {exp.startYear} - {exp.endMonth && exp.endYear ? `${exp.endMonth} ${exp.endYear}` : t('searchStaff.present')}
                                                                </div>
                                                                {exp.description && (
                                                                    <p className={staffStyles.experienceDescription}>{exp.description}</p>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <div className={staffStyles.cardFooter}>
                                        <button
                                            type="button"
                                            onClick={() => handleContactStaff(bartender)}
                                            className={staffStyles.contactButton}
                                        >
                                            {t('searchStaff.contact')}
                                        </button>
                                        <button 
                                            onClick={() => handleCreateEngagement(result)}
                                            className={staffStyles.hiringButton}
                                            disabled={creatingEngagement === result._id || !hasAvailabilityDetails}
                                        >
                                            {creatingEngagement === result._id ? '...' : t('searchStaff.createEngagement')}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Reviews Modal */}
            {selectedStaffForReviews && (
                <div 
                    className={staffStyles.modalOverlay}
                    onClick={() => setSelectedStaffForReviews(null)}
                >
                    <div 
                        className={staffStyles.reviewsModal}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className={staffStyles.modalHeader}>
                            <h2>{t('searchStaff.staffReviews')}</h2>
                            <button 
                                className={staffStyles.closeModal}
                                onClick={() => setSelectedStaffForReviews(null)}
                                aria-label="Close"
                            >
                                ×
                            </button>
                        </div>
                        <div className={staffStyles.modalBody}>
                            <RatingDisplay userId={selectedStaffForReviews} showSummary={false} autoShowReviews={true} hideToggleButton={true} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchStaff;
